import React, { useState } from 'react';
import { api } from '../utils/api';
import './Setup.css';

const STEPS = ['Agency Details', 'Defaults', 'Confirm'];

const DEFAULT_FORM = {
  agencyName:      '8020 Branding Studio',
  gstin:           '',
  pan:             '',
  phone:           '',
  email:           '',
  address:         '',
  gstRate:         18,
  invoicePrefix:   'INV',
  poPrefix:        'PO',
  paymentAdvance:  50,
  paymentInterim:  25,
  paymentFinal:    25,
};

export default function Setup({ onComplete }) {
  const [step,    setStep]    = useState(0);
  const [form,    setForm]    = useState(DEFAULT_FORM);
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  function set(key, value) {
    setForm(f => ({ ...f, [key]: value }));
    setError('');
  }

  function validateStep1() {
    if (!form.agencyName.trim()) return 'Agency name is required.';
    return '';
  }

  function validateStep2() {
    const total = Number(form.paymentAdvance) + Number(form.paymentInterim) + Number(form.paymentFinal);
    if (total !== 100) return `Payment terms must add up to 100% (currently ${total}%).`;
    return '';
  }

  function next() {
    if (step === 0) {
      const err = validateStep1();
      if (err) { setError(err); return; }
    }
    if (step === 1) {
      const err = validateStep2();
      if (err) { setError(err); return; }
    }
    setError('');
    setStep(s => s + 1);
  }

  function back() {
    setError('');
    setStep(s => s - 1);
  }

  async function handleSubmit() {
    setLoading(true);
    setError('');
    try {
      await api.post('/config/setup', form);
      onComplete();
    } catch (err) {
      setError(err.message || 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="setup-page">
      <div className="setup-card">
        <div className="setup-header">
          <div className="login-logo-mark" style={{ width: 36, height: 36 }}>
            <span>80</span><span>20</span>
          </div>
          <div>
            <div className="setup-title">8020 Finance Manager</div>
            <div className="setup-sub">First-time setup</div>
          </div>
        </div>

        {/* Step indicator */}
        <div className="setup-steps">
          {STEPS.map((label, i) => (
            <div key={i} className={`setup-step ${i === step ? 'active' : i < step ? 'done' : ''}`}>
              <div className="step-circle">{i < step ? '✓' : i + 1}</div>
              <div className="step-label">{label}</div>
            </div>
          ))}
        </div>

        <div className="setup-body">
          {error && <div className="setup-error">{error}</div>}

          {/* Step 1 — Agency Details */}
          {step === 0 && (
            <div className="setup-section">
              <div className="form-group">
                <label className="form-label">Agency Name *</label>
                <input className="form-input" value={form.agencyName}
                  onChange={e => set('agencyName', e.target.value)}
                  placeholder="8020 Branding Studio" autoFocus />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">GSTIN</label>
                  <input className="form-input" value={form.gstin}
                    onChange={e => set('gstin', e.target.value)}
                    placeholder="29AAAAA0000A1Z5" />
                </div>
                <div className="form-group">
                  <label className="form-label">PAN</label>
                  <input className="form-input" value={form.pan}
                    onChange={e => set('pan', e.target.value)}
                    placeholder="AAAAA0000A" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" value={form.phone}
                    onChange={e => set('phone', e.target.value)}
                    placeholder="+91 98765 43210" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={form.email}
                    onChange={e => set('email', e.target.value)}
                    placeholder="hello@8020.in" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Address</label>
                <textarea className="form-textarea" value={form.address}
                  onChange={e => set('address', e.target.value)}
                  placeholder="Office address…" rows={2} />
              </div>
            </div>
          )}

          {/* Step 2 — Defaults */}
          {step === 1 && (
            <div className="setup-section">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Default GST Rate (%)</label>
                  <input className="form-input" type="number" value={form.gstRate}
                    onChange={e => set('gstRate', e.target.value)}
                    min={0} max={28} />
                </div>
                <div className="form-group">
                  <label className="form-label">Invoice Prefix</label>
                  <input className="form-input" value={form.invoicePrefix}
                    onChange={e => set('invoicePrefix', e.target.value)}
                    placeholder="INV" />
                </div>
                <div className="form-group">
                  <label className="form-label">PO Prefix</label>
                  <input className="form-input" value={form.poPrefix}
                    onChange={e => set('poPrefix', e.target.value)}
                    placeholder="PO" />
                </div>
              </div>

              <div className="setup-divider">Default Payment Terms</div>
              <p className="setup-hint">These will be pre-filled for every new project. Must total 100%.</p>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Advance (%)</label>
                  <input className="form-input" type="number" value={form.paymentAdvance}
                    onChange={e => set('paymentAdvance', Number(e.target.value))}
                    min={0} max={100} />
                </div>
                <div className="form-group">
                  <label className="form-label">Interim (%)</label>
                  <input className="form-input" type="number" value={form.paymentInterim}
                    onChange={e => set('paymentInterim', Number(e.target.value))}
                    min={0} max={100} />
                </div>
                <div className="form-group">
                  <label className="form-label">Final (%)</label>
                  <input className="form-input" type="number" value={form.paymentFinal}
                    onChange={e => set('paymentFinal', Number(e.target.value))}
                    min={0} max={100} />
                </div>
              </div>

              <div className={`terms-total ${Number(form.paymentAdvance) + Number(form.paymentInterim) + Number(form.paymentFinal) === 100 ? 'valid' : 'invalid'}`}>
                Total: {Number(form.paymentAdvance) + Number(form.paymentInterim) + Number(form.paymentFinal)}%
                {Number(form.paymentAdvance) + Number(form.paymentInterim) + Number(form.paymentFinal) === 100
                  ? ' ✓' : ' — must equal 100%'}
              </div>
            </div>
          )}

          {/* Step 3 — Confirm */}
          {step === 2 && (
            <div className="setup-section">
              <div className="confirm-grid">
                <div className="confirm-row">
                  <span className="confirm-label">Agency Name</span>
                  <span className="confirm-value">{form.agencyName}</span>
                </div>
                {form.gstin && <div className="confirm-row">
                  <span className="confirm-label">GSTIN</span>
                  <span className="confirm-value">{form.gstin}</span>
                </div>}
                {form.pan && <div className="confirm-row">
                  <span className="confirm-label">PAN</span>
                  <span className="confirm-value">{form.pan}</span>
                </div>}
                {form.email && <div className="confirm-row">
                  <span className="confirm-label">Email</span>
                  <span className="confirm-value">{form.email}</span>
                </div>}
                <div className="confirm-row">
                  <span className="confirm-label">Default GST</span>
                  <span className="confirm-value">{form.gstRate}%</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label">Payment Terms</span>
                  <span className="confirm-value">
                    {form.paymentAdvance}% / {form.paymentInterim}% / {form.paymentFinal}%
                  </span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label">Default Login</span>
                  <span className="confirm-value">admin / 8020 (change in Settings)</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="setup-footer">
          {step > 0 && (
            <button className="btn btn-outline" onClick={back} disabled={loading}>
              Back
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < 2
            ? <button className="btn btn-primary" onClick={next}>Continue →</button>
            : <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Saving…' : 'Complete Setup'}
              </button>
          }
        </div>
      </div>
    </div>
  );
}
